import { Button } from './Button.js';

export type PaginationPosition = 'top' | 'bottom';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * 163: which of the two controls this is. Decides the accessible name and the margin — nothing
   * else. The two are otherwise identical on purpose, so an operator learns one control.
   */
  position?: PaginationPosition;
}

/**
 * 163: each control gets its own accessible name, because a list now renders TWO buttons called
 * "Previous" and two called "Next". Without this, "click Next" is ambiguous to a screen reader and
 * to voice control, and a test can only address whichever matched first.
 *
 * `<nav>` is the correct landmark for pagination and gives the name somewhere to live without
 * adding a visible label.
 */
const LABEL: Record<PaginationPosition, string> = {
  top: 'Pagination, above the list',
  bottom: 'Pagination, below the list',
};

export function Pagination({
  page,
  totalPages,
  onPageChange,
  position = 'bottom',
}: PaginationProps) {
  // Unchanged from 113: one page needs no pager, and 163 keeps that in BOTH positions so a short
  // list gains no chrome above the table either.
  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label={LABEL[position]}
      className={`${position === 'top' ? 'mb-4' : 'mt-4'} flex justify-center gap-2`}
    >
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        Previous
      </Button>
      <span className="py-1.5 px-3 text-sm text-slate">
        {page} / {totalPages}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </nav>
  );
}
